// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages } from "react-intl";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { ActionType, IAction, IActionSortTable, raiseAction } from "actions";
import { IEnergyMeter, IModule } from "shared/types/parameter";
import { isSet } from "shared/utils/utils";
import { makeSortHeader } from "guiutils";
import { IState } from "states/states";
import { ITableSort } from "states/guistates";

import EnergyMeter from "view/comp/EnergyMeter";
import { ISiteBarSearchFieldProps } from "./SiteBar";
import EnergyMeterSiteBar from "./EnergyMeterSiteBar";

// sort key of table
const TABLE_NAME = "t_energymeters";

interface IEnergyMeterListExtProps {}

interface IEnergyMeterListProps {
  energymeters: IEnergyMeter[];
  emValues: number[];
  tableSort: ITableSort | undefined;
  showModules: boolean;
  modules: IModule[] | undefined;
  showId: boolean;
  filterText: string;
}

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const showModules =
    Boolean(curUser?.accessRights.controllerAdmin) && Boolean(state.para.parameter?.module.length);
  const props: IEnergyMeterListProps = {
    energymeters: state.para.parameter?.energymeter ?? [],
    emValues: state.data.energymeter,
    tableSort: state.gui.tableSort[TABLE_NAME],
    showModules,
    modules: state.para.parameter?.module,
    showId: Boolean(curUser?.accessRights.setVirtualParameters),
    filterText: state.gui.searchFilterText ?? "",
  };
  return props;
};

interface IEnergyMeterListDispatch {
  onSort: (sort: IActionSortTable) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IEnergyMeterListDispatch = {
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  placeholderSearchFiled: {
    id: "Search for energy meter...",
  },
});

class EnergyMeterListComponent extends React.Component<
  IEnergyMeterListProps & IEnergyMeterListExtProps & IEnergyMeterListDispatch & RoutedProps
> {
  private getSortKey(index: number) {
    const sortCol =
      (this.props.tableSort?.column as keyof IEnergyMeter | "value" | undefined) ?? "name";
    return (
      sortCol === "value" ? this.props.emValues[index]
      : sortCol === "module" ?
        this.props.modules?.find((curItem) => curItem.id === this.props.energymeters[index].module)
          ?.name
      : this.props.energymeters[index]?.[sortCol!]
    );
  }

  private getEnergyMeterList() {
    const filterTextLowerCase = this.props.filterText.toLocaleLowerCase();
    // get list of used energy meter
    const allList = Array.from(Array(this.props.energymeters.length).keys());
    const filteredList = allList.filter((curIndex) => {
      const item = this.props.energymeters[curIndex];
      return (
        item.webActive &&
        (item.name.toLocaleLowerCase().includes(filterTextLowerCase) ||
          item.energySource?.toLocaleLowerCase().includes(filterTextLowerCase))
      );
    });
    const sortedList = filteredList.sort((a, b) => {
      const keyA = this.getSortKey(a);
      const keyB = this.getSortKey(b);
      const sort =
        (isSet(keyA) && isSet(keyB) && keyA > keyB) || (isSet(keyA) && !isSet(keyB)) ? 1 : -1;
      return (this.props.tableSort?.isSortAscending !== false ? sort : -sort) as number;
    });
    return sortedList;
  }

  public render() {
    const hasSources =
      Array.from(new Set(this.props.energymeters.map((curItem) => curItem.energySource))).length >
      1;
    const showNumber = this.props.energymeters.some((curItem) => curItem.number);
    const emIndexList = this.getEnergyMeterList();
    const emItems = emIndexList.map((curItem) => {
      return (
        <EnergyMeter
          key={curItem}
          index={curItem}
          showSource={hasSources}
          showModule={this.props.showModules}
          showNumber={showNumber}
        />
      );
    });

    const searchFieldProps: ISiteBarSearchFieldProps = {
      placeholder: messages.placeholderSearchFiled,
    };

    return (
      <div id="energymeters" className="flex-self flex-content">
        <EnergyMeterSiteBar
          showPeriodSelect={false}
          searchFieldProps={searchFieldProps}
          showBackButton={true}
        />
        <div className="tableSearchHeader">
          <h1>
            <FormattedMessage id="Energy Meters" />
          </h1>
        </div>

        {emItems.length ?
          <table className="datatable fullwidthwhite">
            <thead>
              <tr>
                {this.props.showId && <th className="optional-lg">Id</th>}
                {makeSortHeader(
                  TABLE_NAME,
                  "name",
                  <FormattedMessage id="Name" />,
                  this.props.tableSort ?? { column: "name", isSortAscending: true },
                  this.props.onSort,
                )}
                {this.props.showModules &&
                  makeSortHeader(
                    TABLE_NAME,
                    "module",
                    <FormattedMessage id="Module" />,
                    this.props.tableSort,
                    this.props.onSort,
                    ["optional-md"],
                  )}
                {hasSources &&
                  makeSortHeader(
                    TABLE_NAME,
                    "energySource",
                    <FormattedMessage id="Energy Source" />,
                    this.props.tableSort,
                    this.props.onSort,
                    ["optional-md"],
                  )}
                {showNumber &&
                  makeSortHeader(
                    TABLE_NAME,
                    "number",
                    <FormattedMessage id="Number" />,
                    this.props.tableSort,
                    this.props.onSort,
                    ["optional-md"],
                  )}
                {makeSortHeader(
                  TABLE_NAME,
                  "value",
                  <FormattedMessage id="Value" />,
                  this.props.tableSort,
                  this.props.onSort,
                  ["value-col", "right"],
                )}
              </tr>
            </thead>
            <tbody>{emItems}</tbody>
          </table>
        : <FormattedMessage id="No energy meters present." />}
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(EnergyMeterListComponent));
